/// <amd-module name="Pipeline/Analytics/PassRateCardMetrics/Sources/PassRateCardMetricsSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { IODataQueryResponse, IReportContext, TrendBy } from "Analytics/Shared/Common/Types";
import { getDateFilter } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { ITrendChartData, ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { taskFailures, totalCount, totalFailedCount, useMockData } from "Pipeline/Analytics/Common/MockReportData";
import { PipelineTrendSource } from "Pipeline/Analytics/Common/Sources/PipelineTrendSource";
import { ITopFailingStageAndTaskData } from "Pipeline/Analytics/Common/Types";
import { IVssPageContext } from "VSS/Platform/Context";

export interface IBuildOutcomeData {
    passPercentage: number;
    failedCount: number;
    totalCount: number;
}

export class PassRateMetricsSource extends PipelineTrendSource {
    constructor(pageContext: IVssPageContext) {
        super("PipelineAnalytics_InContext_CardMetricsReport", pageContext);
    }

    public async getTopFailingStageAndTaskData(
        reportContext: IReportContext,
        confValues: ITrendChartReportConfiguration
    ): Promise<ITopFailingStageAndTaskData> {
        return super.getTopFailingStageAndTaskData(reportContext, confValues);
    }

    public async queryPipelineTrendData(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): Promise<ITrendChartData> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildEntityName,
            project: projectId
        } as ODataQueryOptions;

        const filterStr = `filter(${this._getPipelineContextFilter(reportContext)} ${this._getBranchFilter(
            confValues.branchItems
        )} and ${getDateFilter(TrendBy.Days, confValues.period)} and ${this._getTotalPipelineFilter()})`;

        const groupByStr = `groupby((${this._getGroupByCompletedDate()}), aggregate(${this._getTotalCountForAggregation()}, ${this._getPartiallySucceededCountForAggregation()}, ${this._getSucceededCountForAggregation()}, ${this._getFailedCountForAggregation()}))`;
        queryOptions.$apply = `${filterStr}/${groupByStr}`;
        queryOptions.$orderby = ` CompletedDateSK asc `;

        const pipelineTrendData = await this._queryPassRateTrendData(queryOptions, confValues);
        return pipelineTrendData.trendChartData;
    }

    public queryBuildOutcomeData(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): Promise<IBuildOutcomeData> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildEntityName,
            project: projectId
        } as ODataQueryOptions;

        const filterStr = `filter(${this._getPipelineContextFilter(reportContext)} and ${getDateFilter(
            confValues.trendBy,
            confValues.period
        )} and ${this._getTotalPipelineFilter()})`;
        const aggrStr = `aggregate(${this._getTotalCountForAggregation()}, ${this._getSucceededCountForAggregation()}, ${this._getFailedCountForAggregation()}, ${this._getPartiallySucceededCountForAggregation()})`;
        queryOptions.$apply = `${filterStr}/${aggrStr}`;
        return this._queryPassPercentage(queryOptions);
    }

    private async _queryPassPercentage(queryOptions: ODataQueryOptions): Promise<IBuildOutcomeData> {
        if (useMockData) {
            return Promise.resolve({
                failedCount: totalFailedCount,
                totalCount: totalCount,
                passPercentage: this._getPassPercentage(totalCount, totalCount - totalFailedCount, 0)
            } as IBuildOutcomeData);
        } else {
            const data = await this.queryOData(queryOptions);
            if (!data || !data.value) {
                throw new Error("data is empty.");
            }

            const dataValues = data.value as any[];
            const buildOutcomeData: IBuildOutcomeData = { passPercentage: 0, failedCount: 0, totalCount: 0 } as IBuildOutcomeData;

            if (dataValues && dataValues.length > 0) {
                const dataValue = dataValues[0];
                const totalCount = this._parseTotalCount(dataValue);
                const succeededCount = this._parseSucceededCount(dataValue);
                const partiallySucceededCount = this._parsePartiallySucceededCount(dataValue);
                buildOutcomeData.passPercentage = this._getPassPercentage(totalCount, succeededCount, partiallySucceededCount);
                buildOutcomeData.failedCount = this._parseFailedCount(dataValue);
                buildOutcomeData.totalCount = totalCount;
            }

            return buildOutcomeData;
        }
    }
}
